"use client"

// packages/frontend/src/components/ui/reactbits/SplitTextWrapper.tsx

import React from "react"
import { motion, type Variants } from "framer-motion"
import { useTheme } from "../../providers/ThemeProvider"
import { cn } from "@/utils/cn"

interface SplitTextWrapperProps {
  children: string
  className?: string
  delay?: number
  duration?: number
  as?: keyof React.JSX.IntrinsicElements
  disabled?: boolean
}

/**
 * Wrapper for ReactBits SplitText component with theme support
 * Animates text character-by-character for child engagement
 */
export function SplitTextWrapper({
  children,
  className,
  delay = 0,
  duration = 0.5,
  as: Component = "span",
  disabled = false,
}: SplitTextWrapperProps) {
  const { themeType } = useTheme()
  const isChildTheme = themeType === "child"

  // If disabled or not child theme, return text without animation
  if (disabled || !isChildTheme) {
    const ComponentTag = Component as any
    return <ComponentTag className={className}>{children}</ComponentTag>
  }

  // Try to import ReactBits SplitText, fallback to custom implementation
  let SplitText: any = null
  try {
    const reactbits = require("@appletosolutions/reactbits")
    SplitText = reactbits.SplitText || reactbits.default?.SplitText
  } catch (e) {
    // Fallback to custom implementation
  }

  // Custom implementation using Framer Motion
  const characters = children.split("")
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: duration / characters.length,
        delayChildren: delay,
      },
    },
  }

  const characterVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  }

  // If ReactBits component is available, use it
  if (SplitText) {
    return (
      <SplitText className={className} delay={delay} duration={duration}>
        {children}
      </SplitText>
    )
  }

  // Custom implementation
  const ComponentTag = Component as any
  return (
    <ComponentTag className={cn("inline-block", className)}>
      <motion.span
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="inline-block"
      >
        {characters.map((char, index) => (
          <motion.span
            key={index}
            variants={characterVariants}
            className="inline-block"
            style={{ whiteSpace: char === " " ? "pre" : "normal" }}
          >
            {char === " " ? "\u00A0" : char}
          </motion.span>
        ))}
      </motion.span>
    </ComponentTag>
  )
}

